import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./styles/EventPage.module.css";
import { defaultAxios, getPublicEvents } from "./apis";
import { FaSearch } from "react-icons/fa";

function safeTZ(tz) {
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(new Date());
    return tz;
  } catch {
    return undefined;
  }
}

function dayKey(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: safeTZ(tz),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);

  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${y}-${m}-${day}`; 
}


function formatDayTitle(iso, tz) {
  const weekday = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    timeZone: tz || undefined,
  }).format(new Date(iso));

  const same = dayKey(iso, tz) === dayKey(new Date().toISOString(), tz);

  return same ? `Today (${weekday})` : weekday;
}

function formatDateSmall(iso, tz) {
  const d = new Date(iso);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: tz || undefined,
  }).format(d);
}

function formatTimeRange(startISO, endISO, tz) {
  if (!startISO) return "";
  const start = new Date(startISO);
  const end = endISO ? new Date(endISO) : null;

  const tf = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: tz || undefined,
  });

  const s = tf.format(start);
  const e = end ? tf.format(end) : null;
  return e ? `${s}–${e}` : s;
}

function priceLabel(e) {
  if (e?.is_free) return "FREE";
  if (!e?.ticket_price) return "";
  const cur = e.currency || "USD";
  return cur === "USD" ? `$${e.ticket_price}` : `${e.ticket_price} ${cur}`;
}

function badgeForStatus(status) {
  if (status === "LIVE") return { text: "LIVE", tone: "live" };
  if (status === "STARTING_SOON") return { text: "STARTING SOON", tone: "soon" };
  if (status === "UPCOMING") return { text: "UPCOMING", tone: "upcoming" };
  if (status === "ENDED" || status === "DONE") return { text: "ENDED", tone: "ended" };
  return null;
}

function groupByDay(events) {
  const map = new Map();

  for (const e of events) {
    const key = dayKey(e.start_at, e.timezone_str);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(e);
  }

  for (const [k, arr] of map.entries()) {
    arr.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    map.set(k, arr);
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, items]) => ({ dateKey, items }));
}

function EventCard({ e, disableRegister }) {
  const badge = badgeForStatus(e.status);
  const tz = e.timezone_str;

  return (
    <div className={styles.card}>
      <div className={styles.cardImg}>
        {e.card_image ? (
          <img src={e.card_image} alt={e.title} />
        ) : (
          <div className={styles.imgPlaceholder} />
        )}
      </div>

      <div className={styles.cardBody}>
        <div className={styles.cardTop}>
          {badge && (
            <span className={`${styles.badge} ${styles[`badge_${badge.tone}`]}`}>
              {badge.text}
            </span>
          )}
        </div>

        <div className={styles.cardTitle}>{e.title}</div>

        <div className={styles.cardMeta}>
          <div className={styles.cardLine}>
            {formatDayTitle(e.start_at, tz)},{" "}
            {formatTimeRange(e.start_at, e.end_at, tz)} {tz ? `(${tz})` : ""}
          </div>
          <div className={styles.cardLine}>
            {e.event_type === "ONLINE"
              ? e.platform || "Online"
              : e.location_name || e.location || "Location"}
          </div>
          <div className={styles.price}>{priceLabel(e)}</div>
        </div>

        <div className={styles.actions}>
          {disableRegister ? (
            <button className={styles.btnGhost} disabled>
              {e.register_text || "Register"}
            </button>
          ) : e.register_url ? (
            <a
              className={styles.btnGhost}
              href={e.register_url}
              target="_blank"
              rel="noreferrer"
            >
              {e.register_text || "Register"} <span>↗</span>
            </a>
          ) : (
            <button className={styles.btnGhost} disabled>
              Register
            </button>
          )}

          <Link className={styles.detailLink} to={`/events/${e.slug}`}>
            Detail event
          </Link>
        </div>

        <div className={styles.cardAccent} />
      </div>
    </div>
  );
}

export default function EventsPage() {
  const [tab, setTab] = useState("upcoming");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ upcoming: [], past: [] });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        // const res = await getPublicEvents();
        const res = await defaultAxios.get("/api/events/");
        if (!alive) return;
        setData({
          upcoming: res.data?.upcoming || [],
          past: res.data?.past || [],
        });
      } catch (e) {
        if (!alive) return;
        setData({ upcoming: [], past: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const base = tab === "upcoming" ? data.upcoming : data.past;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return base;
    return base.filter((e) =>
      `${e.title || ""} ${e.excerpt || ""}`.toLowerCase().includes(term)
    );
  }, [base, q]);

  const grouped = useMemo(() => groupByDay(filtered), [filtered]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.h1}>Our Event</h1>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${
                tab === "upcoming" ? styles.active : ""
              }`}
              onClick={() => setTab("upcoming")}
            >
              Upcoming
            </button>
            <button
              className={`${styles.tab} ${
                tab === "past" ? styles.active : ""
              }`}
              onClick={() => setTab("past")}
            >
              Past Events
            </button>
          </div>
        </div>

        <div className={styles.search}>
          <span className={styles.searchIcon}><FaSearch /></span>
          <input
            className={styles.searchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search Event"
          />
        </div>
      </div>

      {!loading && grouped.length === 0 ? (
        <div className={styles.emptyWrap}>
          <img
            className={styles.emptyImg}
            src="/images/backgrounds/bg-event-coming-soon.svg"
            alt="Coming soon"
          />
          <div className={styles.emptyText}>
            This page is coming soon! Stay tuned for updates.
          </div>
          <Link className={styles.emptyBtn} to="/">
            Go Back to Homepage
          </Link>
        </div>
      ) : (
        <div className={styles.grid}>
          {loading && <div className={styles.muted}>Loading events…</div>}

          {grouped.map((g, idx) => {
            const first = g.items[0];
            const tz = first?.timezone_str;
            const iso = first?.start_at || `${g.dateKey}T00:00:00Z`;
            const isLast = idx === grouped.length - 1;

            return (
              <div key={g.dateKey} className={styles.groupRow}>
                <div className={`${styles.groupLeft} ${isLast ? styles.groupLeftLast : ""}`}>
                  <span className={styles.groupLine} aria-hidden="true" />
                  <span className={styles.groupDot} aria-hidden="true" />

                  <div className={styles.timelineTitle}>{formatDayTitle(iso, tz)}</div>
                  <div className={styles.timelineSub}>{formatDateSmall(iso, tz)}</div>
                </div>
                <div className={styles.groupRight}>
                {g.items.map((e) => (
                  <EventCard
                    key={e.slug}
                    e={e}
                    disableRegister={
                      tab === "past" || e.status === "ENDED" || e.status === "DONE"
                    }
                  />
                ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
